// Доменные функции API REGWAY CRM.
// Бизнес-команды (publish/mark-sent/accept и т.п.) вызываются как командные
// endpoints; если backend их ещё не реализовал (404/405), выполняется
// безопасный fallback на CRUD/PATCH, а при невозможности — ошибка показывается
// пользователю без падения интерфейса.

import { apiCommand, apiDelete, ApiError, apiGet, apiList, apiPatch, apiPost } from './client';
import type {
    // === WT2 extract ===
    ActCompareResponse,
    Attachment, AuditEvent,
    AutoLinkedItem, AvailableUser,
    ChannelAccount,
    ClientAccount, ClientLtv, Comment,
    CommercialOffer,
    Contact, ControlEvent, Conversation, ConversationRoute,
    CreditPolicy,
    Document, DocumentApproval, DocumentCheck, DocumentTemplate, DocumentVersion,
    ExceptionApproval,
    ExtractedField, ExtractionRun, ExtractionRunDetail,
    FileBlob,
    FinanceAttention,
    FinCandidatesResponse,
    FulfillmentOption,
    GenerationJob,
    InboxDecision, InboxItem,
    Invoice, InvoiceLine,
    IssuedDocument,
    ItemParty, ItemProduct,
    LaunchReadiness, LaunchSnapshot,
    MailAccount,
    Message, MessageRequest,
    Notification, OfferLine,
    OptionSelection, OwnLegalEntity, Party, Payment, PaymentAllocation,
    PriceContext,
    Product,
    RegistryCheck, RequestContact, RequestEntity,
    RequestItem, RequestParty, RequestSnapshot, SupplierQuote, SupplierRequest,
    TakeSnapshotResult, TechnicalCase, UploadFinalResult, User, VersionCompareResult, WorkOrder
} from './types';
// === WT3 docs ===
// === WT5 recon ===
import type {
    ReconciliationRow, ReconciliationRun,
    ReconPreview,
    SupplierFormat,
} from './types';

// ---------- Auth ----------

export const auth = {
  me: () => apiGet<User>('/auth/me/'),
  login: (username: string, password: string) =>
    apiPost<{ ok?: boolean; user?: User }>('/auth/login/', { username, password }),
  logout: () => apiPost('/auth/logout/', {}),
};

// ---------- Справочники ----------

export const parties = {
  list: (params?: Record<string, unknown>) => apiList<Party>('/parties/party/', params),
  get: (id: string) => apiGet<Party>(`/parties/party/${id}/`),
  contacts: (params?: Record<string, unknown>) => apiList<Contact>('/parties/contact/', params),
  clientAccounts: (params?: Record<string, unknown>) => apiList<ClientAccount>('/parties/client-account/', params),
  // === WT4 finance (§12): LTV клиентского счёта ===
  clientAccountLtv: (id: string, params?: { period_from?: string; period_to?: string }) =>
    apiGet<ClientLtv>(`/parties/client-account/${id}/ltv/`, params),
};

// ---------- Заявки ----------

export interface CreateRequestPayload {
  client_account_id: string;
  title: string;
  currency: string;
  contact_id?: string | null;
  document_type?: string;
  country_of_entry?: string;
  payer_id?: string;
  applicant_id?: string;
  manufacturer_id?: string;
  product_name?: string;
  product_description?: string;
  tnved?: string;
  model?: string;
  article?: string;
}

export const requests = {
  list: (params?: Record<string, unknown>) => apiList<RequestEntity>('/requests/request/', params),
  get: (id: string) => apiGet<RequestEntity>(`/requests/request/${id}/`),
  create: (payload: CreateRequestPayload, idemKey?: string) =>
    apiCommand<{ request_id: string; request_number?: string; item_id?: string }>(
      '/requests/request/create-request/', payload, idemKey),

  items: (requestId: string) => apiList<RequestItem>('/requests/request-item/', { request: requestId }),
  /** Обновление полей позиции (PATCH request-item; revision — optimistic lock). */
  updateItem: (id: string, data: Partial<RequestItem> & { revision?: number }) =>
    apiPatch<RequestItem>(`/requests/request-item/${id}/`, data),
  /** Добавление позиции: командный endpoint add-item, fallback — прямой CRUD. */
  addItem: async (requestId: string, data: { title: string; document_type?: string; country_of_entry?: string; currency?: string; revision?: number; payer_id?: string; applicant_id?: string; manufacturer_id?: string; product_name?: string; product_description?: string; tnved?: string }) => {
    try {
      return await apiCommand(`/requests/request/${requestId}/add-item/`, data);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) {
        return apiPost<RequestItem>('/requests/request-item/', { request: requestId, ...data });
      }
      throw e;
    }
  },

  products: (requestId: string) => apiList<Product>('/requests/product/', { request: requestId }),
  createProduct: (data: Partial<Product> & { request: string; name: string }) =>
    apiPost<Product>('/requests/product/', data),
  itemProducts: (itemId: string) => apiList<ItemProduct>('/requests/item-product/', { item: itemId }),
  linkItemProduct: (item: string, product: string) => apiPost('/requests/item-product/', { item, product }),

  contacts: (requestId: string) => apiList<RequestContact>('/requests/request-contact/', { request: requestId }),
  addContact: (request: string, contact: string, role = 'primary', is_primary = true) =>
    apiPost<RequestContact>('/requests/request-contact/', { request, contact, role, is_primary }),
  parties: (requestId: string) => apiList<RequestParty>('/requests/request-party/', { request: requestId }),
  addParty: (request: string, party: string, role: string) =>
    apiPost<RequestParty>('/requests/request-party/', { request, party, role }),
  itemParties: (itemId: string) => apiList<ItemParty>('/requests/item-party/', { item: itemId }),
  snapshots: (requestId: string) => apiList<RequestSnapshot>('/requests/request-snapshot/', { request: requestId }),

  /** Снимок подтверждённых данных заявки (Stage 4): POST take-snapshot {revision} -> {snapshot_id, version}. */
  takeSnapshot: (requestId: string, revision?: number, itemId?: string) =>
    apiCommand<TakeSnapshotResult>(`/requests/request/${requestId}/take-snapshot/`,
      itemId ? { revision, item_id: itemId } : { revision }),

  /** Готовность позиции к запуску (Stage 2). */
  launchReadiness: (itemId: string, legalEntityId?: string) =>
    apiGet<LaunchReadiness>(`/requests/request-item/${itemId}/launch-readiness/`,
                            legalEntityId ? { legal_entity_id: legalEntityId } : undefined),

  /** Ценовой контекст позиции для строки КП (себестоимость, политика, прайс клиента, история). */
  pricePreview: (itemId: string, params: { sale_price?: string | number; currency?: string; quote_id?: string }) =>
    apiGet<PriceContext>(`/requests/request-item/${itemId}/price-preview/`, { ...params }),

  /** Пакетный запуск позиций в работу (команда с Idempotency-Key и revision). */
  launch: (requestId: string, body: { revision: number; item_ids: string[]; assignee_id?: number | string | null; internal_due_at?: string | null; promised_due_at?: string | null }) =>
    apiCommand<{
      request_id: string;
      work_orders: Array<{ work_order_id: string; snapshot_id: string; item_id: string; status: string; revision: number }>;
    }>(`/requests/request/${requestId}/launch/`, body),

  /** Завершение позиции (Stage 7): результат получен, позиция → completed. */
  completeItem: (itemId: string, revision?: number) =>
    apiCommand<{ item_id: string; status: string; revision: number }>(
      `/requests/request-item/${itemId}/complete/`, { revision }),
};

// ---------- Варианты исполнения / техотдел ----------

export const sourcing = {
  options: (params?: Record<string, unknown>) => apiList<FulfillmentOption>('/sourcing/fulfillment-option/', params),
  createOption: (data: Partial<FulfillmentOption> & { item: string; item_revision?: number }) =>
    apiCommand<{ option_id: string; revision: number; status: string }>('/sourcing/fulfillment-option/create/', {
      item_id: data.item,
      revision: data.item_revision,
      country: data.country,
      scheme: data.scheme,
      series_or_batch: data.series_or_batch,
      applicant_mode: data.applicant_mode,
      conditions: data.conditions,
      comment: data.comment,
    }),

  technicalCases: (params?: Record<string, unknown>) => apiList<TechnicalCase>('/sourcing/technical-case/', params),
  technicalCase: (id: string) => apiGet<TechnicalCase>(`/sourcing/technical-case/${id}/`),
  assignableUsers: () => apiList<User>('/sourcing/technical-case/assignable-users/'),
  assignCase: (id: string, assigneeId: number | string, revision?: number, reason = '') =>
    apiCommand(`/sourcing/technical-case/${id}/assign/`, { assignee_id: assigneeId, revision, reason }),
  createTechnicalCase: (option: string, question: string) =>
    apiPost<TechnicalCase>('/sourcing/technical-case/', { option, question }),
  /** Передать вариант на просчёт (команда, fallback — создание TechnicalCase). */
  submitForCalculation: async (optionId: string, question: string, revision?: number) => {
    try {
      return await apiCommand(`/sourcing/fulfillment-option/${optionId}/submit-calculation/`, { question, revision });
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) {
        return apiPost<TechnicalCase>('/sourcing/technical-case/', { option: optionId, question });
      }
      throw e;
    }
  },
  /** Действия по техкейсу: accept/resolve/escalate — команды, fallback — PATCH статуса. */
  caseAction: async (id: string, action: 'accept' | 'resolve' | 'escalate', body: Record<string, unknown> = {}, caseRev?: number) => {
    try {
      return await apiCommand(`/sourcing/technical-case/${id}/${action}/`, { ...body, revision: caseRev });
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) {
        const statusMap = { accept: 'open', resolve: 'resolved', escalate: 'escalated' } as const;
        return apiPatch<TechnicalCase>(`/sourcing/technical-case/${id}/`, { status: statusMap[action], revision: caseRev });
      }
      throw e;
    }
  },

  supplierRequests: (params?: Record<string, unknown>) => apiList<SupplierRequest>('/sourcing/supplier-request/', params),
  createSupplierRequest: (data: { option: string; supplier: string; option_revision?: number; external_number?: string }) =>
    apiCommand<SupplierRequest>('/sourcing/supplier-request/create-request/', {
      option_id: data.option,
      supplier_id: data.supplier,
      revision: data.option_revision,
      external_number: data.external_number,
    }),
  supplierRequestAction: (id: string, action: 'mark-sent' | 'register-response' | 'cancel', body: Record<string, unknown> = {}) =>
    apiCommand(`/sourcing/supplier-request/${id}/${action}/`, body),

  quotes: (params?: Record<string, unknown>) => apiList<SupplierQuote>('/sourcing/supplier-quote/', params),
  createQuote: (data: Partial<SupplierQuote> & { supplier_request: string; request_revision?: number }) =>
    apiCommand<{ quote_id: string; version: number; status: string }>(
      `/sourcing/supplier-request/${data.supplier_request}/create-quote/`,
      { ...data, revision: data.request_revision, supplier_request: undefined },
    ),
  /** Публикация котировки менеджеру (анонимизированная проекция). */
  publishQuote: async (id: string, revision?: number) => {
    try {
      return await apiCommand(`/sourcing/supplier-quote/${id}/publish/`, { revision });
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) {
        return apiPatch<SupplierQuote>(`/sourcing/supplier-quote/${id}/`, { status: 'confirmed', revision });
      }
      throw e;
    }
  },

  selections: (params?: Record<string, unknown>) => apiList<OptionSelection>('/sourcing/option-selection/', params),
  selectQuote: (itemId: string, quoteId: string, revision?: number) =>
    apiCommand<{ selection_id?: string; item_revision?: number }>(
      `/requests/request-item/${itemId}/select-quote/`, { quote_id: quoteId, revision }),

  workOrders: (params?: Record<string, unknown>) => apiList<WorkOrder>('/sourcing/work-order/', params),
  /** Принятие поручения исполнителем/руководителем (команда). */
  acceptWorkOrder: (id: string, revision?: number) =>
    apiCommand(`/sourcing/work-order/${id}/accept/`, { revision }),
  /** Изменение обещанного клиенту срока — причина обязательна (команда). */
  setPromisedDue: (id: string, promisedDueAt: string, reason: string, revision?: number) =>
    apiCommand(`/sourcing/work-order/${id}/set-promised-due/`,
               { promised_due_at: promisedDueAt, reason, revision }),
  launchSnapshots: (params?: Record<string, unknown>) => apiList<LaunchSnapshot>('/sourcing/launch-snapshot/', params),
};

// ---------- Коммерческие предложения ----------

export const offers = {
  list: (params?: Record<string, unknown>) => apiList<CommercialOffer>('/sourcing/commercial-offer/', params),
  get: (id: string) => apiGet<CommercialOffer>(`/sourcing/commercial-offer/${id}/`),
  /** Черновик предложения: команда create-draft, fallback — прямой CRUD. */
  createDraft: async (requestId: string, requestRevision: number | undefined, lines: Array<Record<string, unknown>>) => {
    return apiCommand<{ offer_id: string; version: number; status: string }>(
      '/sourcing/commercial-offer/create-draft/',
      { request_id: requestId, revision: requestRevision, lines },
    );
  },
  lines: (offerId: string) => apiList<OfferLine>('/sourcing/offer-line/', { offer: offerId }),
  /** Предпросмотр КП (может отсутствовать на backend — тогда 404 обрабатывает UI). */
  preview: (id: string) => apiGet<unknown>(`/sourcing/commercial-offer/${id}/preview/`),
  markSent: (id: string, body: { sent_at?: string; channel?: string; contact_id?: string; confirmed?: boolean; revision?: number } = {}) =>
    apiCommand(`/sourcing/commercial-offer/${id}/mark-sent/`, {
      sent_at: body.sent_at ?? new Date().toISOString(),
      confirmed: body.confirmed ?? true,
      ...body,
    }),
  /** Фиксация согласования выбранных строк предложения. */
  accept: (id: string, body: { selected_line_ids?: string[]; line_ids?: string[]; revision?: number; basis?: Record<string, unknown> } = {}) =>
    apiCommand(`/sourcing/commercial-offer/${id}/accept/`, {
      selected_line_ids: body.selected_line_ids ?? body.line_ids ?? [],
      basis: body.basis ?? { source: 'manual', note: 'manual client confirmation' },
      revision: body.revision,
    }),
  reject: (id: string, body: { reason?: string; revision?: number } = {}) =>
    apiCommand(`/sourcing/commercial-offer/${id}/reject/`, body),
};

// ---------- События / комментарии / уведомления ----------

export const workflow = {
  controlEvents: (params?: Record<string, unknown>) => apiList<ControlEvent>('/workflow/control-event/', params),
  /** Завершение события: команда complete, fallback — PATCH статуса. */
  completeEvent: async (id: string, revision?: number) => {
    try {
      return await apiCommand(`/workflow/control-event/${id}/complete/`, { revision });
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) {
        return apiPatch<ControlEvent>(`/workflow/control-event/${id}/`, { status: 'done', revision });
      }
      throw e;
    }
  },

  comments: (requestId: string) => apiList<Comment>('/workflow/comment/', { request: requestId }),
  addComment: (request: string, body: string, visibility = 'internal') =>
    apiPost<Comment>('/workflow/comment/', { request, body, visibility }),

  /** Пауза контрольного события (команда; причина обязательна). */
  pauseEvent: (id: string, reason: string, revision?: number) =>
    apiCommand(`/workflow/control-event/${id}/pause/`, { reason, revision }),
  /** Возобновление события — внутренний срок сдвигается на рабочее время паузы. */
  resumeEvent: (id: string, revision?: number) =>
    apiCommand(`/workflow/control-event/${id}/resume/`, { revision }),

  exceptionApprovals: (params?: Record<string, unknown>) => apiList<ExceptionApproval>('/workflow/exception-approval/', params),
  /** Запрос исключения (например, запуск при недостаточной оплате). */
  requestException: (body: { request_id: string; item_id?: string | null; kind: string; reason: string; amount?: string | null; currency?: string; legal_entity_id?: string | null; scope?: Record<string, unknown> }) =>
    apiCommand<ExceptionApproval>('/workflow/exception-approval/request/', body),
  /** Решение руководителя по исключению (approve=true/false + revision). */
  decideException: (id: string, approve: boolean, reason: string, revision?: number) =>
    apiCommand<ExceptionApproval>(`/workflow/exception-approval/${id}/decide/`, { approve, reason, revision }),

  notifications: (params?: Record<string, unknown>) => apiList<Notification>('/workflow/notification/', params),
  /** Отметить прочитанным: команда mark-read, fallback — PATCH read_at. */
  markRead: async (id: string) => {
    try {
      return await apiCommand(`/workflow/notification/${id}/mark-read/`, {});
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) {
        return apiPatch<Notification>(`/workflow/notification/${id}/`, { read_at: new Date().toISOString() });
      }
      throw e;
    }
  },
};

// ---------- Файлы / аудит ----------

export const documents = {
  fileBlobs: (params?: Record<string, unknown>) => apiList<FileBlob>('/documents/file-blob/', params),
  /** Один FileBlob — опрос статуса проверки после загрузки. */
  getFileBlob: (blobId: string) => apiGet<FileBlob>(`/documents/file-blob/${blobId}/`),
  /** Дескриптор встроенного превью вложения. */
  filePreview: (blobId: string) =>
    apiGet<{
      kind: 'pdf' | 'image' | 'text' | 'html' | 'sheets' | 'unsupported' | 'error';
      name?: string; mime?: string; url?: string; html?: string;
      sheets?: Array<{ name: string; html: string }>;
      reason?: string; message?: string;
    }>(`/documents/file-blob/${blobId}/preview/`),
  /** URL оригинала для просмотра в браузере (inline, не скачивание). */
  downloadInlineUrl: (blobId: string) =>
    `/api/v1/documents/file-blob/${blobId}/download/?inline=1`,
  attachments: (params?: Record<string, unknown>) => apiList<Attachment>('/communications/attachment/', params),
  upload: (file: File, extra: Record<string, string> = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    for (const [k, v] of Object.entries(extra)) fd.append(k, v);
    return apiPost<FileBlob>('/documents/file-blob/upload/', fd);
  },
  downloadUrl: (id: string) => `/api/v1/documents/file-blob/${id}/download/`,

  documents: (params?: Record<string, unknown>) => apiList<Document>('/documents/document/', params),
  documentVersions: (params?: Record<string, unknown>) => apiList<DocumentVersion>('/documents/document-version/', params),

  /** Загрузка финального «полученного» файла новой версией документа (origin='received'):
      multipart/form-data {file, approved_version_id, revision, reason?} + Idempotency-Key ->
      201 {version_id, version, file_blob_id, document_id, scan_status, id}; ошибки 400/403/404/409. */
  uploadFinal: (documentId: string, file: File, fields: { approved_version_id: string; revision?: number | string; reason?: string }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('approved_version_id', fields.approved_version_id);
    if (fields.revision !== undefined && fields.revision !== null) {
      fd.append('revision', String(fields.revision));
    }
    if (fields.reason) {
      fd.append('reason', fields.reason);
    }
    return apiCommand<UploadFinalResult>(`/documents/document/${documentId}/upload-final/`, fd);
  },

  /** Запуск извлечения: ровно один источник — version_id ИЛИ message_id (+ позиция item_id).
      doc_kind — профиль §8: 'invoice' (счёт) | 'act' (акт). */
  extractionStart: (body: { version_id?: string; message_id?: string; item_id?: string; engine?: string; doc_kind?: string }) =>
    apiCommand<ExtractionRun & { run_id?: string }>('/documents/extraction-run/start/', body),
  extractionRuns: (params?: Record<string, unknown>) => apiList<ExtractionRun>('/documents/extraction-run/', params),
  extractionFields: (params?: Record<string, unknown>) => apiList<ExtractedField>('/documents/extracted-field/', params),
  /** Детализация run: поля-кандидаты + текущие значения целей + подтверждения. */
  extractionDetail: (id: string) => apiGet<ExtractionRunDetail>(`/documents/extraction-run/${id}/detail/`),
  /** Пакетное подтверждение полей (accept/keep/correct). 409 — конфликт revision. */
  extractionConfirm: (id: string, body: {
    decisions: Array<{ field_path: string; action: 'accept' | 'keep' | 'correct'; value?: string; target_kind?: string; target_id?: string; target_revision?: number }>;
    target_revision?: number;
    target_revisions?: Record<string, number>;
  }) => apiCommand(`/documents/extraction-run/${id}/confirm/`, body),
  // === WT2 extract ===
  /** §8: кандидаты заявок для счёта/акта + извлечённые значения + предупреждения о дублях. */
  extractionCandidates: (id: string) =>
    apiGet<FinCandidatesResponse>(`/documents/extraction-run/${id}/candidates/`),
  /** §8: сравнение извлечённого акта со счётом — match/mismatch/missing/partial. */
  extractionActCompare: (id: string, invoiceId: string) =>
    apiGet<ActCompareResponse>(`/documents/extraction-run/${id}/act-compare/`, { invoice_id: invoiceId }),
  /** §8: регистрация счёта из прогона — распределение по заявкам. */
  extractionRegisterInvoice: (id: string, body: {
    issuer_id: string; payer_id: string; number?: string; date?: string;
    currency?: string; total?: string; status?: string;
    lines: Array<{ item_id: string; amount: string; description?: string; offer_line_id?: string | null }>;
  }) => apiCommand<{ invoice_id: string; invoice_revision?: number; number?: string; status?: string; file_version_id?: string | null; lines?: Array<{ id: string; item_id: string; amount: string }> }>(
    `/documents/extraction-run/${id}/register-invoice/`, body),
  /** §8: регистрация draft-акта из прогона — строки по invoice_line_id. */
  extractionRegisterAct: (id: string, body: {
    issuer_id: string; payer_id: string; number?: string; date?: string; currency?: string;
    lines: Array<{ invoice_line_id: string; amount: string }>;
  }) => apiCommand<{ id?: string; number?: string; date?: string; currency?: string; status?: string; total?: string; file_version_id?: string | null; lines?: Array<{ id: string; invoice_line_id?: string | null; item_id?: string | null; amount: string }>; warnings?: Array<{ code?: string }> }>(
    `/documents/extraction-run/${id}/register-act/`, body),
  rescan: (id: string) => apiCommand(`/documents/file-blob/${id}/rescan/`, {}),
  // ---------- Контур документов (Stage 4) ----------

  templates: (params?: Record<string, unknown>) => apiList<DocumentTemplate>('/documents/document-template/', params),
  generationJobs: (params?: Record<string, unknown>) => apiList<GenerationJob>('/documents/generation-job/', params),
  generationJob: (id: string) => apiGet<GenerationJob>(`/documents/generation-job/${id}/`),
  /** Запуск генерации макета: POST {template_id, snapshot_id} -> {job_id, status} (Idempotency-Key). */
  createGenerationJob: (body: { template_id: string; snapshot_id: string }) =>
    apiCommand<{ job_id?: string; id?: string; status?: string }>('/documents/generation-job/', body),

  documentChecks: (params?: Record<string, unknown>) => apiList<DocumentCheck>('/documents/document-check/', params),
  documentApprovals: (params?: Record<string, unknown>) => apiList<DocumentApproval>('/documents/document-approval/', params),
  issuedDocuments: (params?: Record<string, unknown>) => apiList<IssuedDocument>('/documents/issued-document/', params),

  /** Проверка версии против снимка: POST {snapshot_id?} -> DocumentCheck. */
  runCheck: (versionId: string, snapshotId?: string) =>
    apiCommand<DocumentCheck>(`/documents/document-version/${versionId}/run-check/`,
                              snapshotId ? { snapshot_id: snapshotId } : {}),
  /** Запрос письменного согласования версии у контакта: POST {contact_id} -> DocumentApproval(pending). */
  requestApproval: (versionId: string, contactId: string) =>
    apiCommand<DocumentApproval>(`/documents/document-version/${versionId}/request-approval/`, { contact_id: contactId }),
  /** Решение по согласованию: {decision, evidence_message_id?|evidence_file_id?, revision?} — approve требует ровно одно evidence. */
  confirmApproval: (approvalId: string, body: { decision: 'approve' | 'reject'; evidence_message_id?: string; evidence_file_id?: string; revision?: number }) =>
    apiCommand<DocumentApproval>(`/documents/document-approval/${approvalId}/confirm/`, body),
  /** Регистрация финального документа позиции: POST {version_id, type, raw_number, issued_date?, expires_at?, supplier_id?} -> IssuedDocument. */
  registerIssued: (itemId: string, body: { version_id: string; type: string; raw_number: string; issued_date?: string; expires_at?: string; supplier_id?: string }) =>
    apiCommand<IssuedDocument>(`/documents/request-item/${itemId}/register-issued/`, body),
  /** Сравнение версии с другой: POST {other_version_id} -> {result, differences:[{field,a,b}], meta}. */
  compareVersions: (versionId: string, otherVersionId: string) =>
    apiCommand<VersionCompareResult>(`/documents/document-version/${versionId}/compare/`, { other_version_id: otherVersionId }),
  /** Решение проверяющего по расхождениям последней проверки (§10.4):
      'accept' -> accepted_mismatch (допуск к регистрации), 'reject' -> mismatch; reason обязателен. */
  resolveCheck: (versionId: string, body: { decision: 'accept' | 'reject'; reason: string }) =>
    apiCommand<DocumentCheck>(`/documents/document-version/${versionId}/resolve-check/`, body),

  // === WT3 docs (ТЗ §9/§10) ===

  /** §9: проверка подтверждённых извлечённых полей финального документа
      (origin='received') против снимка согласованного макета. Прогон
      извлечения (engine='final_rules') стартует автоматически; результат —
      DocumentCheck kind='final_fields' с посполевыми статусами. */
  finalCheck: (versionId: string) =>
    apiCommand<DocumentCheck>(`/documents/document-version/${versionId}/final-check/`, {}),

  /** §10: генерация по типу документа — применимый шаблон выбирается
      сервером (type+country+active, последняя версия); либо явный
      template_id. Несогласованный образец (approved=false) → статус
      'template_unapproved'; недостающие поля → 400 missing_fields. */
  startGenerationByType: (body: { snapshot_id: string; template_id?: string; type?: string; country?: string; regulation?: string }) =>
    apiCommand<{ job_id?: string; id?: string; status?: string; template_id?: string }>(
      '/documents/generation-job/start/', body),

  /** §9: фиксация проверки выданного документа в реестре
      (result: found|not_found|mismatch|unavailable + link/comment). */
  registryCheck: (issuedId: string, body: { result: string; link?: string; comment?: string }) =>
    apiCommand<RegistryCheck>(`/documents/issued-document/${issuedId}/registry-check/`, body),
  /** §9: история проверок реестра выданного документа (новые сверху). */
  registryChecks: (issuedId: string) =>
    apiList<RegistryCheck>(`/documents/issued-document/${issuedId}/registry-check/`),
};

// ---------- Коммуникации / входящие (Stage 3) ----------

export interface InboxDecidePayload {
  message_ids: string[];
  decision: 'new' | 'existing' | 'not_request';
  request_id?: string;
  client_account_id?: string;
  reason?: string;
}

export const communications = {
  /** Очередь необработанных входящих для текущего пользователя. */
  inbox: (params?: Record<string, unknown>) => apiList<InboxItem>('/communications/inbox/', params),
  /** Атомарное решение по входящим: new → создаёт заявку и привязывает сообщения. */
  decide: (body: InboxDecidePayload) =>
    apiCommand<{
      request_id?: string;
      request_number?: string;
      decided?: number;
      linked?: number;
      results?: Array<{ message_id?: string; request_id?: string; status?: string }>;
    }>('/communications/inbox/decide/', body),
  /** История решений по входящим (доступность зависит от роли — 403 обрабатывает UI). */
  inboxDecisions: (params?: Record<string, unknown>) => apiList<InboxDecision>('/communications/inbox-decision/', params),
  /** Недавние входящие, автоматически связанные с заявками (в scope пользователя). */
  inboxAutoLinked: () => apiGet<AutoLinkedItem[]>('/communications/inbox/auto-linked/'),

  messages: (params?: Record<string, unknown>) => apiList<Message>('/communications/message/', params),
  conversations: (params?: Record<string, unknown>) => apiList<Conversation>('/communications/conversation/', params),
  conversationRoutes: (params?: Record<string, unknown>) => apiList<ConversationRoute>('/communications/conversation-route/', params),
  messageLinks: (params?: Record<string, unknown>) => apiList<MessageRequest>('/communications/message-request/', params),
  channelAccounts: (params?: Record<string, unknown>) => apiList<ChannelAccount>('/communications/channel-account/', params),

  /** Явная привязка выбранного сообщения к заявке (link_method='manual'). */
  linkRequest: (messageId: string, requestId: string) =>
    apiCommand(`/communications/message/${messageId}/link-request/`, { request_id: requestId }),
  /** Смена активного маршрута диалога (история сообщений не переносится). */
  routeConversation: (conversationId: string, requestId: string) =>
    apiCommand(`/communications/conversation/${conversationId}/route/`, { request_id: requestId }),
  /** Исходящее сообщение в активной цепочке заявки. */
  sendMessage: (body: { request_id: string; body: string; attachment_ids?: string[] }) =>
    apiCommand<Message>('/communications/message/send/', body),
  /** Повтор провалившейся исходящей отправки (атомарно, без дублей). */
  resendMessage: (messageId: string) =>
    apiCommand<{ requeued?: boolean; delivery_status?: string }>(
      `/communications/message/${messageId}/resend/`),
  /** «Исправить привязку»: request_id — перенос в заявку, без него — во входящие. */
  relinkMessage: (messageId: string, requestId?: string) =>
    apiCommand<{ from_requests?: string[]; to_request?: string }>(
      `/communications/message/${messageId}/relink/`,
      requestId ? { request_id: requestId } : {}),
};

export const audit = {
  events: (params?: Record<string, unknown>) => apiList<AuditEvent>('/audit/audit-event/', params),
};

// ---------- Финансы (Stage 2) ----------

export const finance = {
  ownLegalEntities: (params?: Record<string, unknown>) => apiList<OwnLegalEntity>('/finance/own-legal-entity/', params),
  creditPolicies: (params?: Record<string, unknown>) => apiList<CreditPolicy>('/finance/credit-policy/', params),

  invoices: (params?: Record<string, unknown>) => apiList<Invoice>('/finance/invoice/', params),
  /** Минимальный справочник плательщиков (id, name) для формы счёта — без раскрытия поставщиков. */
  payerChoices: () => apiGet<Array<{ id: string; name: string }>>('/finance/invoice/payer-choices/'),
  /** Регистрация счёта со строками — атомарная команда с Idempotency-Key. */
  registerInvoice: (body: {
    issuer_id: string; payer_id?: string | null; number: string; date: string;
    currency: string; total: string; status?: string;
    lines: Array<{ item_id: string; amount: string; description?: string; offer_line_id?: string | null }>;
  }) => apiCommand<{ invoice_id: string; invoice_revision?: number; number?: string; status?: string; lines?: Array<{ id: string; item_id: string; amount: string }> }>(
    '/finance/invoice/register/', body),
  invoiceLines: (params?: Record<string, unknown>) => apiList<InvoiceLine>('/finance/invoice-line/', params),

  payments: (params?: Record<string, unknown>) => apiList<Payment>('/finance/payment/', params),
  createPayment: (body: Partial<Payment> & { amount: string; currency: string }) =>
    apiPost<Payment>('/finance/payment/', { provider: 'manual', ...body }),
  /** Подтверждение платежа — команда с revision + Idempotency-Key. */
  confirmPayment: (id: string, revision?: number) =>
    apiCommand(`/finance/payment/${id}/confirm/`, { revision }),
  allocations: (params?: Record<string, unknown>) => apiList<PaymentAllocation>('/finance/payment-allocation/', params),
  /** Распределение подтверждённого платежа по строкам счетов (команда, revision). */
  allocate: (paymentId: string, revision: number | undefined, allocations: Array<{ invoice_line_id: string; amount: string }>) =>
    apiCommand(`/finance/payment/${paymentId}/allocate/`, { revision, allocations }),
  refundPayment: (id: string, body: { amount: string; external_id?: string; reversal_allocations?: Array<Record<string, unknown>>; revision?: number }) =>
    apiCommand(`/finance/payment/${id}/refund/`, body),
  cancelAllocations: (id: string, body: { cancellation_allocations: Array<Record<string, unknown>>; revision?: number }) =>
    apiCommand(`/finance/payment/${id}/cancel-allocations/`, body),

  // === WT4 finance (§13): «Требуют внимания» и отметки аннулирования ===
  /** Раздел «Требуют внимания» — вычисляемый список по категориям. */
  attention: () => apiGet<FinanceAttention>('/finance/attention/'),
  /** Пометка счёта «требует аннулирования» (required=true+reason) / снятие (required=false). */
  requireInvoiceCancellation: (id: string, body: { revision?: number; required?: boolean; reason?: string }) =>
    apiCommand<{ document_id: string; document_revision: number; cancellation_required: boolean }>(
      `/finance/invoice/${id}/require-cancellation/`, body),
  /** Пометка акта «требует аннулирования» / снятие. */
  requireActCancellation: (id: string, body: { revision?: number; required?: boolean; reason?: string }) =>
    apiCommand<{ document_id: string; document_revision: number; cancellation_required: boolean }>(
      `/finance/act/${id}/require-cancellation/`, body),
};

export const mailAccounts = {
  list: () => apiList<MailAccount>('/integrations/mail-account/'),
  create: (body: Record<string, unknown>) =>
    apiPost<MailAccount>('/integrations/mail-account/', body),
  update: (id: string, body: Record<string, unknown>) =>
    apiPatch<MailAccount>(`/integrations/mail-account/${id}/`, body),
  remove: (id: string) => apiDelete(`/integrations/mail-account/${id}/`),
  testConnection: (id: string) =>
    apiPost<{ ok?: boolean; error?: string }>(`/integrations/mail-account/${id}/test_connection/`, {}),
  pollNow: (id: string) =>
    apiPost<{ ok?: boolean; error?: string; fetched?: number; new?: number; duplicates?: number }>(
      `/integrations/mail-account/${id}/poll_now/`, {}),
  availableUsers: () => apiList<AvailableUser>('/integrations/mail-account/available-users/'),
};

export const misc = {
  health: () => apiGet<{ status?: string }>('/health/'),
};

// === WT1 workplace ===
import type { AttentionBoard, RequestSummary } from './types';

export const workplace = {
  /** Сводка первого экрана заявки (§5): шапка, финансы по валютам, ситуация, риск. */
  requestSummary: (requestId: string) =>
    apiGet<RequestSummary>(`/requests/request/${requestId}/summary/`),
  /** Экран «Требует внимания» (§6): категории + фильтры assignee/client/document_type/due_from/due_to. */
  attention: (params?: Record<string, unknown>) =>
    apiGet<AttentionBoard>('/workflow/control-event/attention/', params),
};

// === WT5 recon === — сверки поставщиков (ТЗ §14)

export const recon = {
  // Настройки форматов поставщика (CRUD).
  formats: (params?: Record<string, unknown>) =>
    apiList<SupplierFormat>('/finance/supplier-format/', params),
  getFormat: (id: string) => apiGet<SupplierFormat>(`/finance/supplier-format/${id}/`),
  createFormat: (data: Partial<SupplierFormat> & { supplier: string }) =>
    apiPost<SupplierFormat>('/finance/supplier-format/', data),
  updateFormat: (id: string, data: Partial<SupplierFormat>) =>
    apiPatch<SupplierFormat>(`/finance/supplier-format/${id}/`, data),

  // Прогоны сверок.
  runs: (params?: Record<string, unknown>) =>
    apiList<ReconciliationRun>('/finance/reconciliation-run/', params),
  run: (id: string) => apiGet<ReconciliationRun>(`/finance/reconciliation-run/${id}/`),
  /** Загрузка Excel-файла сверки: {file, supplier_id, period_from, period_to, format_id?} -> прогон draft. */
  upload: (file: File, fields: {
    supplier_id: string; period_from: string; period_to: string; format_id?: string;
  }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('supplier_id', fields.supplier_id);
    fd.append('period_from', fields.period_from);
    fd.append('period_to', fields.period_to);
    if (fields.format_id) fd.append('format_id', fields.format_id);
    return apiPost<ReconciliationRun>('/finance/reconciliation-run/upload/', fd);
  },
  /** Предпросмотр: первые N строк, распознанные колонки, ошибки формата. */
  preview: (id: string, limit = 20) =>
    apiGet<ReconPreview>(`/finance/reconciliation-run/${id}/preview/`, { limit }),
  /** Постановка прогона в очередь сверки (Idempotency-Key генерируется). */
  start: (id: string, revision?: number) =>
    apiCommand<ReconciliationRun>(`/finance/reconciliation-run/${id}/start/`,
      revision !== undefined ? { revision } : {}),
  /** Строки результата с фильтром ?result= / ?side=. */
  rows: (id: string, params?: Record<string, unknown>) =>
    apiList<ReconciliationRow>(`/finance/reconciliation-run/${id}/rows/`, params),
  /** Разбор исключения: {row_id, decision, comment}; финоперации не меняются. */
  resolveRow: (runId: string, body: {
    row_id: string; decision: 'accept' | 'reject' | 'clarify'; comment: string;
  }) => apiCommand<ReconciliationRow>(
    `/finance/reconciliation-run/${runId}/resolve-row/`, body),
};
